import React, { useState, useEffect } from 'react';
import { Home, Menu, X, Settings, TrendingUp, User, MessageCircle, Users, IndianRupee, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
// import HomeButton from './HomeButton'; // Removed as per user request
import { useNavigate, useLocation } from 'react-router-dom';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  
  const goToHome = () => {
    navigate('/');
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = () => {
    // Close mobile menu first
    setIsMobileMenuOpen(false);
    
    // If we're on the main page, scroll to top
    // If we're on any other page (like LinkedIn Insights), navigate to home
    if (location.pathname === '/') {
      scrollToTop();
    } else {
      goToHome();
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSectionClick = (sectionId) => {
    closeMobileMenu();
    
    // If we're on the home page, just scroll to section
    if (location.pathname === '/') {
      const element = document.querySelector(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Navigate to home page and then scroll to section
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  return (
    <header className="dark-header">
      <div className="header-container">
        {/* Left Section - Logo & Brand Name */}
        <div className="flex items-center gap-2 md:gap-3">
          {/* Creative Logo with Animation */}
          <div 
            onClick={handleHomeClick}
            className="cursor-pointer group flex items-center gap-2 md:gap-3"
          >
            <div className="relative">
              {/* Logo Container with Glow Effect - Responsive sizes */}
              <div className="relative w-12 h-12 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-lg p-1 md:p-2">
                <img 
                  src="/shanso-logo-transparent.png"
                  alt="ShansoConsult Logo"
                  className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
            
            {/* Company Name - Now Visible on All Screens */}
            <div className="flex flex-col">
              <span className="text-base md:text-xl lg:text-2xl font-bold group-hover:text-turquoise-300 transition-all duration-300" style={{color: '#14b8a6'}}>
                ShansoConsult
              </span>
            </div>
          </div>
        </div>

        {/* Mobile Menu Button - Hamburger Icon */}
        <button
          onClick={toggleMobileMenu}
          className="mobile-menu-btn md:hidden flex items-center justify-center w-10 h-10 bg-turquoise-500 hover:bg-turquoise-400 text-white rounded-lg transition-all duration-300"
          aria-label="Toggle Menu"
          title="Toggle Menu"
        >
          {isMobileMenuOpen ? (
            <X size={24} />
          ) : (
            <Menu size={24} />
          )}
        </button>
      </div>
      
      {/* Desktop Navigation */}
      <nav className="dark-nav-desktop">
        <button 
          onClick={handleHomeClick}
          className="dark-nav-link-enhanced"
          title={location.pathname === '/' ? 'Scroll to Top' : 'Go to Home Page'}
        >
          <Home size={16} />
          <span>Home</span>
        </button>
        <button 
          onClick={() => handleSectionClick('#about')}
          className="dark-nav-link-enhanced"
        >
          <User size={16} />
          <span>About</span>
        </button>
        <button 
          onClick={() => handleSectionClick('#services')}
          className="dark-nav-link-enhanced"
        >
          <Settings size={16} />
          <span>Services</span>
        </button>
        <button 
          onClick={() => { navigate('/pricing'); closeMobileMenu(); }}
          className="dark-nav-link-enhanced"
        >
          <IndianRupee size={16} />
          <span>Pricing</span>
        </button>
        <button 
          onClick={() => { navigate('/iso42001-tools'); closeMobileMenu(); }}
          className="dark-nav-link-enhanced"
        >
          <TrendingUp size={16} />
          <span>ISO 42001</span>
        </button>
        <button 
          onClick={() => { navigate('/community'); closeMobileMenu(); }}
          className="dark-nav-link-enhanced"
        >
          <Users size={16} />
          <span>Community</span>
        </button>
        <button 
          onClick={() => handleSectionClick('#faq')}
          className="dark-nav-link-enhanced"
        >
          <HelpCircle size={16} />
          <span>FAQ</span>
        </button>
        <button 
          onClick={() => handleSectionClick('#contact')}
          className="dark-nav-link-enhanced"
        >
          <MessageCircle size={16} />
          <span>Contact</span>
        </button>
        {/* HomeButton removed as per user request */}
        <Button 
          className="btn-primary"
          onClick={() => {
            navigate('/book-consultation');
            closeMobileMenu();
          }}
        >
          Book Strategy Call
        </Button>
      </nav>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-nav-overlay">
          <nav className="mobile-nav">
            <button 
              onClick={handleHomeClick}
              className="mobile-nav-link-enhanced"
              title={location.pathname === '/' ? 'Scroll to Top' : 'Go to Home Page'}
            >
              <Home size={16} />
              <span>Home</span>
            </button>
            <button 
              onClick={() => handleSectionClick('#about')}
              className="mobile-nav-link-enhanced"
            >
              <User size={16} />
              <span>About</span>
            </button>
            <button 
              onClick={() => handleSectionClick('#services')}
              className="mobile-nav-link-enhanced"
            >
              <Settings size={16} />
              <span>Services</span>
            </button>
            <button 
              onClick={() => { navigate('/pricing'); closeMobileMenu(); }}
              className="mobile-nav-link-enhanced"
            >
              <IndianRupee size={16} />
              <span>Pricing</span>
            </button>
            <button 
              onClick={() => { navigate('/iso42001-tools'); closeMobileMenu(); }}
              className="mobile-nav-link-enhanced"
            >
              <TrendingUp size={16} />
              <span>ISO 42001</span>
            </button>
            <button 
              onClick={() => { navigate('/community'); closeMobileMenu(); }}
              className="mobile-nav-link-enhanced"
            >
              <Users size={16} />
              <span>Community</span>
            </button>
            <button 
              onClick={() => handleSectionClick('#faq')}
              className="mobile-nav-link-enhanced"
            >
              <HelpCircle size={16} />
              <span>FAQ</span>
            </button>
            <button 
              onClick={() => handleSectionClick('#contact')}
              className="mobile-nav-link-enhanced"
            >
              <MessageCircle size={16} />
              <span>Contact</span>
            </button>
            
            <div className="mobile-nav-buttons">
              {/* HomeButton removed as per user request */}
              <Button 
                className="btn-primary w-full"
                onClick={() => {
                  navigate('/book-consultation');
                  closeMobileMenu();
                }}
              >
                Book Strategy Call
              </Button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;